import Box from '@mui/material/Box';
import {
  BarChart as MUIBarChart,
  BarChartProps as MUIBarChartProps,
} from '@mui/x-charts/BarChart';
import { Button, Typography } from '@mui/material';
import { formatCount } from '@util/number';
import { useBarChart } from './hooks';
import { BarChartEntry } from '@views/pages/Dashboard/types';
import { ChartNoResultPlaceholder } from '@views/pages/Dashboard/components';
import { Link } from 'react-router-dom';

type IBarChartProps = {
  entries: BarChartEntry[];
  total?: number;
  report_id?: string;
  otherThreshold?: number;
} & Omit<MUIBarChartProps, 'series' | 'dataset'>;

export const BarChart = ({
  entries,
  report_id,
  otherThreshold = 999,
  height = 270,
  // width = 530,
  total,
  ...rest
}: IBarChartProps) => {
  const { finalEntries, totalValue, valueKeys, colors } = useBarChart({
    entries,
    otherThreshold,
    total,
  });

  if (totalValue <= 0 || finalEntries.length === 0) {
    return <ChartNoResultPlaceholder />;
  }

  return (
    <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 200,
        }}
      >
        <Typography variant='h4'>{formatCount(totalValue)}</Typography>
        {report_id && (
          <Button
            variant='text'
            component={Link}
            to={`/reporting/items/${report_id}`}
            target='_blank'
          >
            VIEW FULL REPORT
          </Button>
        )}
      </Box>

      <Box
        sx={{
          maxHeight: height,
          overflowY: 'auto',
          flex: 1,
          width: '100%',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: theme => theme.palette.background.paper,
            py: 0.5,
            px: 1,
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {valueKeys.map((key, idx) => (
            <Box
              key={key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  bgcolor: colors?.[idx],
                }}
              />
              <Typography variant='caption' sx={{ lineHeight: 1 }}>
                {key}
              </Typography>
            </Box>
          ))}
        </Box>

        <MUIBarChart
          colors={colors}
          dataset={finalEntries}
          layout='horizontal'
          series={valueKeys.map(key => ({
            dataKey: key,
            label: key,
            stack: 'total',
          }))}
          yAxis={[
            {
              scaleType: 'band',
              dataKey: 'label',
              categoryGapRatio: 0.3,
              barGapRatio: 0,
              width: 100,
            },
          ]}
          xAxis={[
            {
              tickLabelInterval: () => true,
              valueFormatter: (value: number) => formatCount(value),
              tickLabelStyle: { fill: '#000', fontSize: 12 },
              height: 30,
            },
          ]}
          slotProps={{ legend: { sx: { display: 'none' } } }}
          height={
            finalEntries.length * 25 > height ? finalEntries.length * 25 : 230
          }
          margin={{ right: 30 }}
          {...rest}
        />
      </Box>
    </Box>
  );
};
